import { useCallback, useEffect } from "react";
import { useAppContext } from "../../classes/contexts/AppContext";
import { useDashboard } from "../../classes/contexts/DashboardContext";
import { usePatientContext } from "../../classes/contexts/PatientContext";
import WidgetBox from "../../components/widgetBox/WidgetBox";
import {
  defaultPatientDashboardLayout,
  noReportsPatientDashboardLayout,
} from "../../config/layoutConfig";
import ClinicalHistory from "./components/clinicalHistory/ClinicalHistory";
import MedicalImage from "./components/medicalImage/MedicalImage";
import PainAssessment from "./components/painAssessment/PainAssessment";
import PatientMorphology from "./components/patientMorphology/PatientMorphology";
import PersonalInformation from "./components/personalInformation/PersonalInformation";
import Reports from "./components/reports/Reports";
import "./PatientDashboard.css";

const WIDGET_COMPONENTS = {
  personalInfo: PersonalInformation,
  patientMorphology: PatientMorphology,
  medicalImage: MedicalImage,
  painAssessment: PainAssessment,
  clinicalHistory: ClinicalHistory,
  reports: Reports,
};

const PatientDashboard = () => {
  const { hideSidebars } = useAppContext();
  const { handleMaximizeWidget, maximizedWidget, getTranslatedTitle } =
    useDashboard();
  const { reports, reportsLoading } = usePatientContext();

  // Use default layout while loading (reports === null) or when reports exist.
  // Only switch to the compact layout once we know for certain there are none.
  const hasReports =
    reportsLoading ||
    reports === null ||
    (Array.isArray(reports) && reports.length > 0);
  const activeLayout = hasReports
    ? defaultPatientDashboardLayout
    : noReportsPatientDashboardLayout;

  // Hide sidebars - no more widget picker
  useEffect(() => {
    hideSidebars();
  }, [hideSidebars]);

  const renderWidget = useCallback(
    widget => {
      const WidgetComponent = WIDGET_COMPONENTS[widget.contentType];
      if (!WidgetComponent) return null;

      const isMaximized = maximizedWidget === widget.id;
      const isHidden = maximizedWidget && maximizedWidget !== widget.id;

      return (
        <div
          key={widget.id}
          className={`dashboard-widget ${isMaximized ? "maximized" : ""} ${isHidden ? "hidden" : ""}`}
          style={{
            gridColumn: isMaximized ? "1 / -1" : widget.column,
            gridRow: isMaximized ? "1 / -1" : widget.row,
          }}
        >
          <WidgetBox
            title={getTranslatedTitle(widget.title)}
            noVerticalPadding
            noHorizontalPadding
            isClosable={false}
            isMaximizable={widget.contentType !== "personalInfo"}
            onMaximize={() => handleMaximizeWidget(widget.id)}
            isMaximized={isMaximized}
          >
            <div className="widget-content">
              <WidgetComponent />
            </div>
          </WidgetBox>
        </div>
      );
    },
    [maximizedWidget, handleMaximizeWidget, getTranslatedTitle]
  );

  return (
    <div className="patient-dashboard fixed-layout">
      {activeLayout.map(renderWidget)}
    </div>
  );
};

export default PatientDashboard;
